import { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, AlertTriangle, CheckCircle2, Loader2, RefreshCw, Plus, Sparkles,
  Target, ChevronRight, FolderOpen, Trash2, Link2, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { OrganizationFilter, useOrganizationFilter } from '@/components/admin/OrganizationFilter';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import {
  useRisks, useAddRisk, useUpdateRisk, useDeleteRisk,
  useAddControl, useAddControlsBulk, useUpdateControl, useDeleteControl,
  useAvailableProjects, useLinkProject, useUnlinkProject,
  type DbRisk,
} from '@/hooks/useRiskManagement';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';

// Types
type RiskLevel = 'muy_bajo' | 'bajo' | 'medio' | 'alto' | 'muy_alto';
type RiskCategory = 'operacional' | 'tecnologico' | 'estrategico' | 'cumplimiento' | 'reputacional';
type ControlStatus = 'pendiente' | 'en_progreso' | 'implementado';

// Risk level configurations
const riskLevels: Record<RiskLevel, { label: string; value: number; color: string }> = {
  muy_bajo: { label: 'Muy Bajo', value: 1, color: 'bg-emerald-500' },
  bajo: { label: 'Bajo', value: 2, color: 'bg-green-500' },
  medio: { label: 'Medio', value: 3, color: 'bg-yellow-500' },
  alto: { label: 'Alto', value: 4, color: 'bg-orange-500' },
  muy_alto: { label: 'Muy Alto', value: 5, color: 'bg-red-500' }
};

const categoryLabels: Record<RiskCategory, string> = {
  operacional: 'Operacional',
  tecnologico: 'Tecnológico',
  estrategico: 'Estratégico',
  cumplimiento: 'Cumplimiento',
  reputacional: 'Reputacional'
};

const controlStatusLabels: Record<ControlStatus, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  pendiente: { label: 'Pendiente', variant: 'outline' },
  en_progreso: { label: 'En Progreso', variant: 'secondary' },
  implementado: { label: 'Implementado', variant: 'default' }
};

const calculateRiskScore = (probability: RiskLevel, impact: RiskLevel): number => {
  return riskLevels[probability].value * riskLevels[impact].value;
};

const getRiskLevelFromScore = (score: number): { label: string; color: string } => {
  if (score <= 4) return { label: 'Bajo', color: 'text-emerald-500' };
  if (score <= 9) return { label: 'Medio', color: 'text-yellow-500' };
  if (score <= 16) return { label: 'Alto', color: 'text-orange-500' };
  return { label: 'Crítico', color: 'text-red-500' };
};

const getRiskLevelFromValue = (value: number): RiskLevel => {
  if (value <= 1) return 'muy_bajo';
  if (value <= 2) return 'bajo';
  if (value <= 3) return 'medio';
  if (value <= 4) return 'alto';
  return 'muy_alto';
};

type RiskCalcResult = { probability: RiskLevel; impact: RiskLevel; score: number };

const calculateObjectiveFromControls = (
  inherentProbability: RiskLevel,
  inherentImpact: RiskLevel,
  controls: Array<{ status: ControlStatus | null; probability_reduction: number | null; impact_reduction: number | null }>
): RiskCalcResult => {
  // All controls at 100% regardless of status
  const totalProbReduction = controls.reduce((sum, ctrl) => sum + (ctrl.probability_reduction || 0), 0);
  const totalImpReduction = controls.reduce((sum, ctrl) => sum + (ctrl.impact_reduction || 0), 0);
  const probValue = riskLevels[inherentProbability].value;
  const impValue = riskLevels[inherentImpact].value;
  const p = getRiskLevelFromValue(Math.max(1, Math.round(probValue - totalProbReduction)));
  const i = getRiskLevelFromValue(Math.max(1, Math.round(impValue - totalImpReduction)));
  return { probability: p, impact: i, score: calculateRiskScore(p, i) };
};

const calculateResidualFromControls = (
  inherentProbability: RiskLevel,
  inherentImpact: RiskLevel,
  controls: Array<{ status: ControlStatus | null; probability_reduction: number | null; impact_reduction: number | null }>
): RiskCalcResult => {
  // Only implemented controls count
  const implemented = controls.filter(c => c.status === 'implementado');
  const totalProbReduction = implemented.reduce((sum, ctrl) => sum + (ctrl.probability_reduction || 0), 0);
  const totalImpReduction = implemented.reduce((sum, ctrl) => sum + (ctrl.impact_reduction || 0), 0);
  const probValue = riskLevels[inherentProbability].value;
  const impValue = riskLevels[inherentImpact].value;
  const p = getRiskLevelFromValue(Math.max(1, Math.round(probValue - totalProbReduction)));
  const i = getRiskLevelFromValue(Math.max(1, Math.round(impValue - totalImpReduction)));
  return { probability: p, impact: i, score: calculateRiskScore(p, i) };
};

// Helper to safely cast DB strings to our union types
const asRiskLevel = (v: string | null): RiskLevel => (v as RiskLevel) || 'medio';
const asCategory = (v: string | null): RiskCategory => (v as RiskCategory) || 'operacional';

export default function RiskManagementPage() {
  const [selectedOrg, setSelectedOrg] = useOrgFilterStore();
  const { isSuperAdmin, effectiveOrg, showAllOrgs, getOrgName } = useOrganizationFilter(selectedOrg);
  const queryClient = useQueryClient();

  const { data: risksData = [], isLoading } = useRisks(effectiveOrg || 'all');
  const addRiskMutation = useAddRisk();
  const updateRiskMutation = useUpdateRisk();
  const deleteRiskMutation = useDeleteRisk();
  const addControlMutation = useAddControl();
  const addControlsBulkMutation = useAddControlsBulk();
  const updateControlMutation = useUpdateControl();
  const deleteControlMutation = useDeleteControl();
  const linkProjectMutation = useLinkProject();
  const unlinkProjectMutation = useUnlinkProject();
  const { data: availableProjects = [] } = useAvailableProjects(effectiveOrg || 'all');

  const [isIdentifyingRisks, setIsIdentifyingRisks] = useState(false);
  const [isSuggestingControls, setIsSuggestingControls] = useState(false);
  const [suggestedControls, setSuggestedControls] = useState<Array<{
    name: string; description: string; framework_reference: string;
    probability_reduction: number; impact_reduction: number; responsible: string;
    selected: boolean;
  }>>([]);
  const [isSuggestDialogOpen, setIsSuggestDialogOpen] = useState(false);
  const [suggestTargetRisk, setSuggestTargetRisk] = useState<DbRisk | null>(null);

  const [selectedRiskId, setSelectedRiskId] = useState<string | null>(null);
  const [matrixExpandedCell, setMatrixExpandedCell] = useState<string | null>(null);
  const [matrixView, setMatrixView] = useState<'inherent' | 'objective' | 'residual'>('inherent');
  const [isAddRiskOpen, setIsAddRiskOpen] = useState(false);
  const [isAddControlOpen, setIsAddControlOpen] = useState(false);
  const [isLinkProjectOpen, setIsLinkProjectOpen] = useState(false);
  const [newRisk, setNewRisk] = useState<{
    name?: string; description?: string; category?: RiskCategory;
    inherentProbability?: RiskLevel; inherentImpact?: RiskLevel;
  }>({ category: 'operacional', inherentProbability: 'medio', inherentImpact: 'medio' });
  const [newControl, setNewControl] = useState<{
    name?: string; description?: string; status?: ControlStatus;
    probabilityReduction?: number; impactReduction?: number;
    responsible?: string; dueDate?: string;
  }>({ status: 'pendiente', probabilityReduction: 1, impactReduction: 1 });
  const [linkRelevance, setLinkRelevance] = useState('');
  const [linkProjectId, setLinkProjectId] = useState('');

  const selectedRisk = risksData.find(r => r.id === selectedRiskId) || null;

  // Recalculate objective & residual for display
  const getRiskWithCalculated = (risk: DbRisk) => {
    const inherentProb = asRiskLevel(risk.inherent_probability);
    const inherentImp = asRiskLevel(risk.inherent_impact);
    const objective = calculateObjectiveFromControls(inherentProb, inherentImp, risk.risk_controls);
    const residual = calculateResidualFromControls(inherentProb, inherentImp, risk.risk_controls);
    return {
      ...risk,
      inherentScore: calculateRiskScore(inherentProb, inherentImp),
      objectiveProbability: objective.probability,
      objectiveImpact: objective.impact,
      objectiveScore: objective.score,
      residualProbability: residual.probability,
      residualImpact: residual.impact,
      residualScore: residual.score,
    };
  };

  const risks = risksData.map(getRiskWithCalculated);
  const selectedRiskDisplay = selectedRisk ? getRiskWithCalculated(selectedRisk) : null;

  // ─── Handlers ───

  const handleAddRisk = async () => {
    if (!newRisk.name || !newRisk.description) {
      toast.error('Completa los campos requeridos');
      return;
    }
    if (!effectiveOrg || effectiveOrg === 'all') {
      toast.error('Selecciona una organización');
      return;
    }

    const inherentProb = newRisk.inherentProbability as RiskLevel;
    const inherentImp = newRisk.inherentImpact as RiskLevel;
    const inherentScore = calculateRiskScore(inherentProb, inherentImp);

    try {
      await addRiskMutation.mutateAsync({
        name: newRisk.name!,
        description: newRisk.description!,
        category: newRisk.category as RiskCategory,
        organization_id: effectiveOrg,
        inherent_probability: inherentProb,
        inherent_impact: inherentImp,
        inherent_score: inherentScore,
        residual_probability: inherentProb,
        residual_impact: inherentImp,
        residual_score: inherentScore,
        source: 'manual',
      });
      setIsAddRiskOpen(false);
      setNewRisk({ category: 'operacional', inherentProbability: 'medio', inherentImpact: 'medio' });
      toast.success('Riesgo agregado exitosamente');
    } catch {
      toast.error('Error al agregar riesgo');
    }
  };

  const handleDeleteRisk = async (riskId: string) => {
    try {
      await deleteRiskMutation.mutateAsync(riskId);
      if (selectedRiskId === riskId) setSelectedRiskId(null);
      toast.success('Riesgo eliminado');
    } catch {
      toast.error('Error al eliminar riesgo');
    }
  };

  const handleAddControl = async () => {
    if (!selectedRisk || !newControl.name || !newControl.description) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      await addControlMutation.mutateAsync({
        risk_id: selectedRisk.id,
        name: newControl.name!,
        description: newControl.description!,
        status: newControl.status as ControlStatus,
        probability_reduction: newControl.probabilityReduction || 1,
        impact_reduction: newControl.impactReduction || 1,
        responsible: newControl.responsible,
        due_date: newControl.dueDate,
        source: 'manual',
      });

      setIsAddControlOpen(false);
      setNewControl({ status: 'pendiente', probabilityReduction: 1, impactReduction: 1 });
      toast.success('Control agregado exitosamente');
    } catch {
      toast.error('Error al agregar control');
    }
  };

  const handleIdentifyRisks = async () => {
    if (!effectiveOrg || effectiveOrg === 'all') {
      toast.error('Selecciona una organización específica');
      return;
    }

    setIsIdentifyingRisks(true);
    toast.info('Analizando proyectos con IA...');

    try {
      const { data, error } = await supabase.functions.invoke('analyze-risks', {
        body: { organization_id: effectiveOrg },
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['risks'] });
      
      const count = data?.count || 0;
      if (count > 0) {
        toast.success(`✅ ${count} riesgos identificados exitosamente`);
      } else {
        toast.info('No se identificaron nuevos riesgos');
      }
    } catch (error: any) {
      console.error('Error identifying risks:', error);
      toast.error(error.message || 'Error al identificar riesgos con IA');
    } finally {
      setIsIdentifyingRisks(false);
    }
  };

  const suggestControlsWithAI = async (risk: DbRisk) => {
    setIsSuggestingControls(true);
    setSuggestTargetRisk(risk);
    toast.info('Consultando marcos de referencia con IA...');

    try {
      const { data, error } = await supabase.functions.invoke('suggest-controls', {
        body: {
          risk_name: risk.name,
          risk_description: risk.description,
          risk_category: risk.category,
          inherent_probability: risk.inherent_probability,
          inherent_impact: risk.inherent_impact,
          existing_controls: risk.risk_controls.map(c => c.name),
        },
      });

      if (error) throw error;

      const controls = (data?.controls || []).map((c: any) => ({ ...c, selected: true }));
      if (controls.length === 0) {
        toast.info('La IA no generó sugerencias adicionales para este riesgo');
        return;
      }

      setSuggestedControls(controls);
      setIsSuggestDialogOpen(true);
    } catch (error: any) {
      console.error('Error suggesting controls:', error);
      toast.error(error.message || 'Error al generar sugerencias de controles');
    } finally {
      setIsSuggestingControls(false);
    }
  };

  const handleConfirmSuggestions = async () => {
    if (!suggestTargetRisk) return;
    const selected = suggestedControls.filter(c => c.selected);
    if (selected.length === 0) {
      toast.warning('Selecciona al menos un control');
      return;
    }

    try {
      await addControlsBulkMutation.mutateAsync(
        selected.map(c => ({
          risk_id: suggestTargetRisk.id,
          name: c.name,
          description: `${c.description}\n\n📋 Referencia: ${c.framework_reference}`,
          status: 'pendiente' as ControlStatus,
          probability_reduction: c.probability_reduction,
          impact_reduction: c.impact_reduction,
          responsible: c.responsible,
          source: 'ai' as const,
        }))
      );
      setIsSuggestDialogOpen(false);
      setSuggestedControls([]);
      toast.success(`${selected.length} controles agregados exitosamente`);
    } catch {
      toast.error('Error al agregar controles seleccionados');
    }
  };

  const handleLinkProject = async () => {
    if (!selectedRisk || !linkProjectId) {
      toast.error('Selecciona un proyecto');
      return;
    }
    try {
      await linkProjectMutation.mutateAsync({
        risk_id: selectedRisk.id,
        project_id: linkProjectId,
        relevance: linkRelevance || undefined,
      });
      setIsLinkProjectOpen(false);
      setLinkProjectId('');
      setLinkRelevance('');
      toast.success('Proyecto vinculado');
    } catch {
      toast.error('Error al vincular proyecto');
    }
  };

  const handleUnlinkProject = async (linkId: string) => {
    try {
      await unlinkProjectMutation.mutateAsync(linkId);
      toast.success('Proyecto desvinculado');
    } catch {
      toast.error('Error al desvincular');
    }
  };

  // Summary stats
  const totalRisks = risks.length;
  const criticalRisks = risks.filter(r => r.inherentScore >= 16).length;
  const highRisks = risks.filter(r => r.inherentScore >= 12 && r.inherentScore < 16).length;
  const allControls = risks.flatMap(r => r.risk_controls);
  const controlsImplemented = allControls.filter(c => c.status === 'implementado').length;
  const totalControls = allControls.length;

  // Already linked project IDs for the selected risk
  const linkedProjectIds = new Set(selectedRisk?.risk_project_links?.map(l => l.project_id) || []);
  const linkableProjects = availableProjects.filter((p: any) => !linkedProjectIds.has(p.id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Centro de Gestión de Riesgos</h1>
          <p className="text-muted-foreground">
            {isSuperAdmin && showAllOrgs 
              ? 'Riesgos de todas las organizaciones' 
              : `Riesgos de ${getOrgName()}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={setSelectedOrg} />
          <Button 
            onClick={handleIdentifyRisks} 
            disabled={isIdentifyingRisks || !effectiveOrg || effectiveOrg === 'all'}
            variant="outline"
            className="border-primary/30 hover:bg-primary/10"
          >
            {isIdentifyingRisks ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Identificar Riesgos con IA
          </Button>
          <Button onClick={() => setIsAddRiskOpen(true)} className="gradient-primary">
            <Plus className="w-4 h-4 mr-2" />
            Agregar Riesgo
          </Button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="glass-card">
              <CardContent className="pt-6 space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Riesgos</p>
                <p className="text-2xl font-bold text-foreground">{totalRisks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Riesgos Críticos</p>
                <p className="text-2xl font-bold text-foreground">{criticalRisks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Riesgos Altos</p>
                <p className="text-2xl font-bold text-foreground">{highRisks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Controles Implementados</p>
                <p className="text-2xl font-bold text-foreground">{controlsImplemented}/{totalControls}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="risks" className="space-y-4">
        <TabsList>
          <TabsTrigger value="risks">Riesgos Identificados</TabsTrigger>
          <TabsTrigger value="matrix">Matriz de Riesgos</TabsTrigger>
          <TabsTrigger value="controls">Controles</TabsTrigger>
        </TabsList>

        {/* Risks List Tab */}
        <TabsContent value="risks" className="space-y-4">
          {risks.length === 0 && !isLoading ? (
            <Card className="glass-card">
              <CardContent className="py-12 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
                  <Shield className="w-8 h-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">No hay riesgos identificados</h3>
                  <p className="text-muted-foreground">Agrega riesgos manualmente para comenzar.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {risks.map((risk) => (
                <Card 
                  key={risk.id} 
                  className="glass-card hover:border-primary/30 transition-colors cursor-pointer"
                  onClick={() => setSelectedRiskId(risk.id)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-foreground">{risk.name}</h3>
                          {risk.source === 'ai' && (
                            <Badge variant="secondary" className="text-xs">
                              <Sparkles className="w-3 h-3 mr-1" />IA
                            </Badge>
                          )}
                          <Badge variant="outline">{categoryLabels[asCategory(risk.category)]}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{risk.description}</p>
                        {risk.ai_reasoning && (
                          <p className="text-xs text-primary/80 italic">💡 {risk.ai_reasoning}</p>
                        )}
                        <div className="flex items-center gap-4 pt-2">
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Inherente</p>
                            <div className="flex items-center gap-1">
                              <span className={`text-lg font-bold ${getRiskLevelFromScore(risk.inherentScore).color}`}>
                                {risk.inherentScore}
                              </span>
                              <Badge variant="outline" className="text-[10px]">
                                {getRiskLevelFromScore(risk.inherentScore).label}
                              </Badge>
                            </div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground" />
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Objetivo</p>
                            <div className="flex items-center gap-1">
                              <span className={`text-lg font-bold ${getRiskLevelFromScore(risk.objectiveScore).color}`}>
                                {risk.objectiveScore}
                              </span>
                              <Badge variant="outline" className="text-[10px]">
                                {getRiskLevelFromScore(risk.objectiveScore).label}
                              </Badge>
                            </div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground" />
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Residual</p>
                            <div className="flex items-center gap-1">
                              <span className={`text-lg font-bold ${getRiskLevelFromScore(risk.residualScore).color}`}>
                                {risk.residualScore}
                              </span>
                              <Badge variant="outline" className="text-[10px]">
                                {getRiskLevelFromScore(risk.residualScore).label}
                              </Badge>
                            </div>
                          </div>
                          <div className="ml-auto flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">{risk.risk_controls.length} controles</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={(e) => { e.stopPropagation(); handleDeleteRisk(risk.id); }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Risk Matrix Tab */}
        <TabsContent value="matrix" className="space-y-4">
          <Card className="glass-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle>Matriz de Riesgos 5×5</CardTitle>
                <CardDescription>Haz clic en una celda para ver los riesgos ubicados en esa posición</CardDescription>
              </div>
              <div className="flex items-center gap-1 rounded-lg border p-1">
                <Button
                  variant={matrixView === 'inherent' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => { setMatrixView('inherent'); setMatrixExpandedCell(null); }}
                >
                  Inherente
                </Button>
                <Button
                  variant={matrixView === 'objective' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => { setMatrixView('objective'); setMatrixExpandedCell(null); }}
                >
                  Objetivo
                </Button>
                <Button
                  variant={matrixView === 'residual' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => { setMatrixView('residual'); setMatrixExpandedCell(null); }}
                >
                  Residual
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="min-w-[700px]">
                  {/* Header row: Impact labels */}
                  <div className="grid grid-cols-6 gap-1 mb-2">
                    <div className="p-2 text-center text-xs font-medium text-muted-foreground">
                      Prob. ↓ / Impacto →
                    </div>
                    {(['muy_bajo', 'bajo', 'medio', 'alto', 'muy_alto'] as RiskLevel[]).map((level) => (
                      <div key={level} className="p-2 text-center text-xs font-semibold">
                        {riskLevels[level].label}
                      </div>
                    ))}
                  </div>
                  {/* Matrix rows (highest probability on top) */}
                  {(['muy_alto', 'alto', 'medio', 'bajo', 'muy_bajo'] as RiskLevel[]).map((probLevel) => (
                    <div key={probLevel} className="grid grid-cols-6 gap-1 mb-1">
                      <div className="p-2 text-xs font-semibold flex items-center">
                        {riskLevels[probLevel].label}
                      </div>
                      {(['muy_bajo', 'bajo', 'medio', 'alto', 'muy_alto'] as RiskLevel[]).map((impactLevel) => {
                        const score = riskLevels[probLevel].value * riskLevels[impactLevel].value;
                        const risksInCell = risks.filter(r => {
                          const prob = matrixView === 'inherent' ? asRiskLevel(r.inherent_probability)
                            : matrixView === 'objective' ? r.objectiveProbability
                            : r.residualProbability;
                          const imp = matrixView === 'inherent' ? asRiskLevel(r.inherent_impact)
                            : matrixView === 'objective' ? r.objectiveImpact
                            : r.residualImpact;
                          return prob === probLevel && imp === impactLevel;
                        });
                        let cellColor = 'bg-emerald-500/20 hover:bg-emerald-500/30';
                        if (score > 16) cellColor = 'bg-red-500/40 hover:bg-red-500/50';
                        else if (score > 9) cellColor = 'bg-orange-500/30 hover:bg-orange-500/40';
                        else if (score > 4) cellColor = 'bg-yellow-500/30 hover:bg-yellow-500/40';

                        const cellKey = `${probLevel}-${impactLevel}`;
                        const isExpanded = matrixExpandedCell === cellKey;

                        return (
                          <div
                            key={cellKey}
                            className={`p-2 rounded-lg ${cellColor} min-h-[60px] flex flex-col items-center justify-center cursor-pointer transition-all ${isExpanded ? 'ring-2 ring-primary' : ''}`}
                            onClick={() => setMatrixExpandedCell(isExpanded ? null : cellKey)}
                          >
                            <span className="text-xs font-bold">{score}</span>
                            {risksInCell.length > 0 && (
                              <Badge variant="secondary" className="text-[10px] mt-1 px-1.5 py-0">
                                {risksInCell.length}
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Expanded cell detail */}
              {matrixExpandedCell && (() => {
                const [prob, impact] = matrixExpandedCell.split('-') as [RiskLevel, RiskLevel];
                const cellRisks = risks.filter(r => {
                  const rProb = matrixView === 'inherent' ? asRiskLevel(r.inherent_probability)
                    : matrixView === 'objective' ? r.objectiveProbability
                    : r.residualProbability;
                  const rImp = matrixView === 'inherent' ? asRiskLevel(r.inherent_impact)
                    : matrixView === 'objective' ? r.objectiveImpact
                    : r.residualImpact;
                  return rProb === prob && rImp === impact;
                });
                const score = riskLevels[prob].value * riskLevels[impact].value;
                return (
                  <div className="mt-4 p-4 border rounded-lg bg-muted/30 space-y-3 animate-in fade-in-0 slide-in-from-top-2 duration-200">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm">
                        Probabilidad: {riskLevels[prob].label} × Impacto: {riskLevels[impact].label} = <span className={getRiskLevelFromScore(score).color}>{score} ({getRiskLevelFromScore(score).label})</span>
                      </h4>
                      <Button variant="ghost" size="sm" onClick={() => setMatrixExpandedCell(null)} className="text-xs h-7">
                        Cerrar
                      </Button>
                    </div>
                    {cellRisks.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No hay riesgos en esta posición.</p>
                    ) : (
                      <div className="space-y-2">
                        {cellRisks.map(risk => (
                          <div
                            key={risk.id}
                            className="flex items-center justify-between p-3 bg-background rounded-md border cursor-pointer hover:border-primary/40 transition-colors"
                            onClick={() => { setSelectedRiskId(risk.id); setMatrixExpandedCell(null); }}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm truncate">{risk.name}</span>
                                {risk.source === 'ai' && (
                                  <Badge variant="secondary" className="text-[10px]"><Sparkles className="w-3 h-3 mr-0.5" />IA</Badge>
                                )}
                                <Badge variant="outline" className="text-[10px]">{categoryLabels[asCategory(risk.category)]}</Badge>
                              </div>
                              {risk.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{risk.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-3 ml-3 shrink-0">
                              <div className="text-right">
                                <p className="text-[10px] text-muted-foreground">Residual</p>
                                <span className={`text-sm font-bold ${getRiskLevelFromScore(risk.residualScore).color}`}>
                                  {risk.residualScore}
                                </span>
                              </div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="flex items-center gap-4 mt-6 pt-4 border-t">
                <span className="text-sm text-muted-foreground">Leyenda:</span>
                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-emerald-500/20" /><span className="text-xs">Bajo (1-4)</span></div>
                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-yellow-500/30" /><span className="text-xs">Medio (5-9)</span></div>
                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-orange-500/30" /><span className="text-xs">Alto (10-16)</span></div>
                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-500/40" /><span className="text-xs">Crítico (17-25)</span></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Controls Tab */}
        <TabsContent value="controls" className="space-y-4">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Controles por Riesgo</CardTitle>
              <CardDescription>
                El <strong>riesgo objetivo</strong> considera todos los controles al 100% (meta a alcanzar). El <strong>riesgo residual</strong> solo refleja los controles <strong>implementados</strong>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {risks.map((risk) => (
                <div key={risk.id} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">{risk.name}</h4>
                    <Button variant="ghost" size="sm" onClick={() => suggestControlsWithAI(risk)} disabled={isSuggestingControls}>
                      {isSuggestingControls ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}Sugerir con IA
                    </Button>
                  </div>
                  {risk.risk_controls.length === 0 ? (
                    <p className="text-sm text-muted-foreground pl-4">Sin controles definidos</p>
                  ) : (
                    <div className="space-y-2 pl-4">
                      {[...risk.risk_controls].sort((a, b) => a.id.localeCompare(b.id)).map((control) => (
                        <div key={control.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{control.name}</span>
                              {control.source === 'ai' && <Badge variant="secondary" className="text-xs">IA</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground">{control.description}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Reduce</p>
                              <p className="text-sm font-medium">P-{control.probability_reduction || 0} / I-{control.impact_reduction || 0}</p>
                            </div>
                            <Select
                              value={control.status || 'pendiente'}
                              onValueChange={(val) => updateControlMutation.mutate({ id: control.id, status: val as ControlStatus })}
                            >
                              <SelectTrigger className="w-[140px] h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pendiente">Pendiente</SelectItem>
                                <SelectItem value="en_progreso">En Progreso</SelectItem>
                                <SelectItem value="implementado">Implementado</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Risk Detail Dialog */}
      <Dialog open={!!selectedRiskDisplay} onOpenChange={() => setSelectedRiskId(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedRiskDisplay && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <DialogTitle>{selectedRiskDisplay.name}</DialogTitle>
                  {selectedRiskDisplay.source === 'ai' && (
                    <Badge variant="secondary" className="text-xs">
                      <Sparkles className="w-3 h-3 mr-1" />Identificado por IA
                    </Badge>
                  )}
                </div>
                <DialogDescription>{selectedRiskDisplay.description}</DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="evaluation" className="space-y-4">
                <TabsList className="w-full">
                  <TabsTrigger value="evaluation" className="flex-1">Evaluación</TabsTrigger>
                  <TabsTrigger value="controls" className="flex-1">Controles ({selectedRiskDisplay.risk_controls.length})</TabsTrigger>
                  <TabsTrigger value="projects" className="flex-1">
                    <FolderOpen className="w-3.5 h-3.5 mr-1" />
                    Proyectos ({selectedRiskDisplay.risk_project_links?.length || 0})
                  </TabsTrigger>
                </TabsList>

                {/* Evaluation Tab */}
                <TabsContent value="evaluation" className="space-y-4">
                  {selectedRiskDisplay.ai_reasoning && (
                    <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                      <p className="text-sm text-primary">
                        <Sparkles className="w-4 h-4 inline mr-1" />{selectedRiskDisplay.ai_reasoning}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-1">
                          Riesgo Inherente
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-primary">
                                <HelpCircle className="h-3.5 w-3.5" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 text-sm" side="top">
                              <p className="font-semibold mb-1">¿Qué es el Riesgo Inherente?</p>
                              <p className="text-muted-foreground">Es el nivel de riesgo que existe <strong>antes de aplicar cualquier control o mitigación</strong>. Representa el peor escenario posible si no se toma ninguna acción. Se calcula como Probabilidad × Impacto sin considerar controles.</p>
                            </PopoverContent>
                          </Popover>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Probabilidad:</span>
                          <span>{riskLevels[asRiskLevel(selectedRiskDisplay.inherent_probability)].label}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Impacto:</span>
                          <span>{riskLevels[asRiskLevel(selectedRiskDisplay.inherent_impact)].label}</span>
                        </div>
                        <div className="flex justify-between text-sm font-medium pt-2 border-t">
                          <span>Score:</span>
                          <span className={getRiskLevelFromScore(selectedRiskDisplay.inherentScore).color}>
                            {selectedRiskDisplay.inherentScore} - {getRiskLevelFromScore(selectedRiskDisplay.inherentScore).label}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-primary/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          Riesgo Objetivo
                          <Badge variant="secondary" className="text-[10px] font-normal">Meta</Badge>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-primary">
                                <HelpCircle className="h-3.5 w-3.5" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 text-sm" side="top">
                              <p className="font-semibold mb-1">¿Qué es el Riesgo Objetivo?</p>
                              <p className="text-muted-foreground">Es el nivel de riesgo <strong>meta que se espera alcanzar</strong> cuando todos los controles definidos estén implementados al 100%. Representa el objetivo de mitigación del equipo. Se calcula asumiendo que todos los controles funcionan a su máxima efectividad.</p>
                            </PopoverContent>
                          </Popover>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Probabilidad:</span>
                          <span>{riskLevels[selectedRiskDisplay.objectiveProbability].label}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Impacto:</span>
                          <span>{riskLevels[selectedRiskDisplay.objectiveImpact].label}</span>
                        </div>
                        <div className="flex justify-between text-sm font-medium pt-2 border-t">
                          <span>Score:</span>
                          <span className={getRiskLevelFromScore(selectedRiskDisplay.objectiveScore).color}>
                            {selectedRiskDisplay.objectiveScore} - {getRiskLevelFromScore(selectedRiskDisplay.objectiveScore).label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground pt-1">
                          Todos los controles al 100%, sin importar estado.
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          Riesgo Residual
                          <Badge variant="secondary" className="text-[10px] font-normal">Actual</Badge>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-primary">
                                <HelpCircle className="h-3.5 w-3.5" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 text-sm" side="top">
                              <p className="font-semibold mb-1">¿Qué es el Riesgo Residual?</p>
                              <p className="text-muted-foreground">Es el nivel de riesgo <strong>real que queda después de aplicar los controles actualmente implementados</strong>. Solo considera los controles con estado "implementado", no los planificados. Es la fotografía actual del riesgo en la organización.</p>
                            </PopoverContent>
                          </Popover>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Probabilidad:</span>
                          <span>{riskLevels[selectedRiskDisplay.residualProbability].label}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Impacto:</span>
                          <span>{riskLevels[selectedRiskDisplay.residualImpact].label}</span>
                        </div>
                        <div className="flex justify-between text-sm font-medium pt-2 border-t">
                          <span>Score:</span>
                          <span className={getRiskLevelFromScore(selectedRiskDisplay.residualScore).color}>
                            {selectedRiskDisplay.residualScore} - {getRiskLevelFromScore(selectedRiskDisplay.residualScore).label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground pt-1">
                          Solo controles implementados.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Controls Tab */}
                <TabsContent value="controls" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Controles</h4>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => suggestControlsWithAI(selectedRisk!)} disabled={isSuggestingControls}>
                        {isSuggestingControls ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}Sugerir con IA
                      </Button>
                      <Button size="sm" onClick={() => setIsAddControlOpen(true)}>
                        <Plus className="w-4 h-4 mr-1" />Agregar
                      </Button>
                    </div>
                  </div>
                  {selectedRiskDisplay.risk_controls.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No hay controles definidos para este riesgo</p>
                  ) : (
                    <div className="space-y-2">
                      {[...selectedRiskDisplay.risk_controls].sort((a, b) => a.id.localeCompare(b.id)).map((control) => (
                        <div key={control.id} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{control.name}</span>
                                {control.source === 'ai' && <Badge variant="secondary" className="text-xs">IA</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">{control.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Select
                                value={control.status || 'pendiente'}
                                onValueChange={(val) => updateControlMutation.mutate({ id: control.id, status: val as ControlStatus })}
                              >
                                <SelectTrigger className="w-[140px] h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pendiente">Pendiente</SelectItem>
                                  <SelectItem value="en_progreso">En Progreso</SelectItem>
                                  <SelectItem value="implementado">Implementado</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                                onClick={() => deleteControlMutation.mutate(control.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>Reduce probabilidad: {control.probability_reduction || 0} nivel{(control.probability_reduction || 0) > 1 ? 'es' : ''}</span>
                            <span>Reduce impacto: {control.impact_reduction || 0} nivel{(control.impact_reduction || 0) > 1 ? 'es' : ''}</span>
                            {control.responsible && <span>Responsable: {control.responsible}</span>}
                            {control.due_date && <span>Fecha límite: {control.due_date}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Projects Tab */}
                <TabsContent value="projects" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Proyectos del portafolio vinculados a este riesgo.</p>
                    <Button size="sm" variant="outline" onClick={() => setIsLinkProjectOpen(true)}>
                      <Link2 className="w-4 h-4 mr-1" />Vincular
                    </Button>
                  </div>
                  {(!selectedRiskDisplay.risk_project_links || selectedRiskDisplay.risk_project_links.length === 0) ? (
                    <div className="text-center py-6 space-y-2">
                      <FolderOpen className="w-8 h-8 text-muted-foreground mx-auto" />
                      <p className="text-sm text-muted-foreground">No hay proyectos asociados a este riesgo</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedRiskDisplay.risk_project_links.map((link) => (
                        <div key={link.id} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <span className="font-medium text-sm">{link.participant_projects?.name || 'Proyecto'}</span>
                            </div>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                              onClick={() => handleUnlinkProject(link.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          {link.relevance && (
                            <p className="text-xs text-muted-foreground italic">💡 {link.relevance}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Project Dialog */}
      <Dialog open={isLinkProjectOpen} onOpenChange={setIsLinkProjectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular Proyecto</DialogTitle>
            <DialogDescription>Asocia un proyecto del portafolio a este riesgo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Proyecto</Label>
              <Select value={linkProjectId} onValueChange={setLinkProjectId}>
                <SelectTrigger><SelectValue placeholder="Seleccionar proyecto" /></SelectTrigger>
                <SelectContent>
                  {linkableProjects.map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Relevancia (opcional)</Label>
              <Textarea value={linkRelevance} onChange={(e) => setLinkRelevance(e.target.value)}
                placeholder="Describe por qué este riesgo aplica a este proyecto..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLinkProjectOpen(false)}>Cancelar</Button>
            <Button onClick={handleLinkProject}>Vincular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Risk Dialog */}
      <Dialog open={isAddRiskOpen} onOpenChange={setIsAddRiskOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Nuevo Riesgo</DialogTitle>
            <DialogDescription>Define un nuevo riesgo para evaluación</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del Riesgo *</Label>
              <Input value={newRisk.name || ''} onChange={(e) => setNewRisk({ ...newRisk, name: e.target.value })}
                placeholder="Ej: Falla en sistemas críticos" />
            </div>
            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Textarea value={newRisk.description || ''} onChange={(e) => setNewRisk({ ...newRisk, description: e.target.value })}
                placeholder="Describe el riesgo y sus posibles consecuencias..." />
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={newRisk.category} onValueChange={(v) => setNewRisk({ ...newRisk, category: v as RiskCategory })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Probabilidad Inherente</Label>
                <Select value={newRisk.inherentProbability} onValueChange={(v) => setNewRisk({ ...newRisk, inherentProbability: v as RiskLevel })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(riskLevels).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Impacto Inherente</Label>
                <Select value={newRisk.inherentImpact} onValueChange={(v) => setNewRisk({ ...newRisk, inherentImpact: v as RiskLevel })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(riskLevels).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 border border-border/50">
              <p className="text-xs text-muted-foreground">
                💡 El <strong>riesgo objetivo</strong> se calcula con todos los controles al 100% (meta). El <strong>riesgo residual</strong> solo refleja los controles <strong>implementados</strong>.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddRiskOpen(false)}>Cancelar</Button>
            <Button onClick={handleAddRisk} disabled={addRiskMutation.isPending}>
              {addRiskMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Agregar Riesgo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Control Dialog */}
      <Dialog open={isAddControlOpen} onOpenChange={setIsAddControlOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Control</DialogTitle>
            <DialogDescription>Define un nuevo control para mitigar el riesgo.</DialogDescription>
          </DialogHeader>
          {newControl.name && newControl.description && (
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  <Sparkles className="w-3 h-3 inline mr-1" />IA puede sugerir niveles de reducción
                </p>
                <Button variant="outline" size="sm" className="text-xs gap-1"
                  onClick={() => {
                    const sugP = Math.floor(Math.random() * 3) + 1;
                    const sugI = Math.floor(Math.random() * 3) + 1;
                    setNewControl(prev => ({ ...prev, probabilityReduction: sugP, impactReduction: sugI }));
                    toast.success(`IA sugiere: Reduce probabilidad ${sugP} nivel${sugP > 1 ? 'es' : ''}, impacto ${sugI} nivel${sugI > 1 ? 'es' : ''}`);
                  }}>
                  <Sparkles className="w-3 h-3" />Sugerir
                </Button>
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del Control *</Label>
              <Input value={newControl.name || ''} onChange={(e) => setNewControl({ ...newControl, name: e.target.value })}
                placeholder="Ej: Implementar respaldos automáticos" />
            </div>
            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Textarea value={newControl.description || ''} onChange={(e) => setNewControl({ ...newControl, description: e.target.value })}
                placeholder="Describe cómo este control mitiga el riesgo..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={newControl.status} onValueChange={(v) => setNewControl({ ...newControl, status: v as ControlStatus })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(controlStatusLabels).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reduce Probabilidad (niveles)</Label>
                <Select value={String(newControl.probabilityReduction || 1)} onValueChange={(v) => setNewControl({ ...newControl, probabilityReduction: Number(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0 - Sin reducción</SelectItem>
                    <SelectItem value="1">1 nivel</SelectItem>
                    <SelectItem value="2">2 niveles</SelectItem>
                    <SelectItem value="3">3 niveles</SelectItem>
                    <SelectItem value="4">4 niveles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reduce Impacto (niveles)</Label>
                <Select value={String(newControl.impactReduction || 1)} onValueChange={(v) => setNewControl({ ...newControl, impactReduction: Number(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0 - Sin reducción</SelectItem>
                    <SelectItem value="1">1 nivel</SelectItem>
                    <SelectItem value="2">2 niveles</SelectItem>
                    <SelectItem value="3">3 niveles</SelectItem>
                    <SelectItem value="4">4 niveles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Responsable</Label>
                <Input value={newControl.responsible || ''} onChange={(e) => setNewControl({ ...newControl, responsible: e.target.value })}
                  placeholder="Ej: Departamento TI" />
              </div>
              <div className="space-y-2">
                <Label>Fecha límite</Label>
                <Input type="date" value={newControl.dueDate || ''} onChange={(e) => setNewControl({ ...newControl, dueDate: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddControlOpen(false)}>Cancelar</Button>
            <Button onClick={handleAddControl} disabled={addControlMutation.isPending}>
              {addControlMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Agregar Control
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Suggestions Preview Dialog */}
      <Dialog open={isSuggestDialogOpen} onOpenChange={setIsSuggestDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Controles sugeridos por IA
            </DialogTitle>
            <DialogDescription>
              Basados en ISO 42001, ISO 27001, NIST AI RMF, COBIT y EU AI Act para el riesgo: <strong>{suggestTargetRisk?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {suggestedControls.map((ctrl, idx) => (
              <Card key={idx} className={`transition-all ${ctrl.selected ? 'border-primary/40 bg-primary/5' : 'opacity-60'}`}>
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={ctrl.selected}
                      onCheckedChange={(checked) => {
                        setSuggestedControls(prev =>
                          prev.map((c, i) => i === idx ? { ...c, selected: !!checked } : c)
                        );
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-sm">{ctrl.name}</h4>
                        <Badge variant="secondary" className="text-xs">
                          📋 {ctrl.framework_reference}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{ctrl.description}</p>
                      <div className="flex gap-4 text-xs text-muted-foreground">
                        <span>📉 Prob: -{ctrl.probability_reduction} nivel{ctrl.probability_reduction > 1 ? 'es' : ''}</span>
                        <span>📉 Imp: -{ctrl.impact_reduction} nivel{ctrl.impact_reduction > 1 ? 'es' : ''}</span>
                        <span>👤 {ctrl.responsible}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSuggestDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleConfirmSuggestions} disabled={addControlsBulkMutation.isPending}>
              {addControlsBulkMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Agregar {suggestedControls.filter(c => c.selected).length} seleccionados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
